// src/components/PageLayout.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Avatar,
  Tooltip,
  useTheme,
  styled,
  useMediaQuery,
  Menu,
  MenuItem,
  Paper,
  Chip,
  Stack,
  alpha,
  Divider,
  Badge,
} from "@mui/material";
import {
  useNavigate,
  useLocation,
  Link as RouterLink,
  matchPath,
} from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BarChartIcon from "@mui/icons-material/BarChart";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";

import { useAuth } from "../contexts/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../contexts/firebase/config";
import sintanetLogo from "../assets/sintanet-logo.png";
import brunoImg from "../assets/bruno.png";

const FULL_DRAWER_WIDTH = 310;
const COLLAPSED_DRAWER_WIDTH = 92;
const APPBAR_HEIGHT = 88;

const DrawerHeader = styled(Box)(({ theme }) => ({
  minHeight: APPBAR_HEIGHT,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2),
  overflow: "hidden",
}));

export default function PageLayout({ children, contentPaper = true }) {
  const { currentUser, userType, logout } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isDark = theme.palette.mode === "dark";

  const [mobileOpen, setMobileOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (userType === "partner" && currentUser?.uid) {
      getDoc(doc(db, "empresas", currentUser.uid))
        .then((snap) => {
          if (snap.exists()) setCompanyName(snap.data().nomeEmpresa || "");
        })
        .catch(console.error);
    }
  }, [userType, currentUser]);

  useEffect(() => {
    if (!mdUp) setCollapsed(false);
  }, [mdUp]);

  const toggleMobile = () => setMobileOpen((prev) => !prev);
  const openMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);
  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  const handleLogout = async () => {
    closeMenu();
    await logout();
    navigate("/login", { replace: true });
  };

  const userName = useMemo(() => {
    if (userType === "partner") return companyName || "";
    const raw = currentUser?.displayName || currentUser?.email || "";
    return String(raw).split("@")[0] || "";
  }, [userType, companyName, currentUser]);

  const avatarInitial = useMemo(
    () => (userName ? userName.charAt(0).toUpperCase() : "A"),
    [userName]
  );

  const roleLabel = useMemo(() => {
    if (userType === "admin") return "Administrador";
    if (userType === "partner") return "Parceiro";
    if (userType === "loja") return "Loja";
    return "Utilizador";
  }, [userType]);

  const navItems = useMemo(() => {
    if (userType === "admin") {
      return [
        {
          text: "Dashboard",
          icon: <DashboardIcon />,
          to: "/admin",
        },

        {
          text: "Parceiros",
          icon: <PeopleIcon />,
          to: "/admin/parceiros",
        },

        {
          text: "Pagamentos",
          icon: <AttachMoneyIcon />,
          to: "/admin/pagamentos",
        },

        {
          text: "Relatório",
          icon: <BarChartIcon />,
          to: "/admin/relatorio",
        },

        {
          text: "Comunicações",
          icon: <EmailRoundedIcon />,
          to: "/admin/comunicacoes",
        },

        {
          text: "Notificações",
          icon: <NotificationsActiveIcon />,
          to: "/admin/notificacoes",
          badge: 3,
        },
      ];
    }

    if (userType === "loja") {
      return [
        { text: "Perfil", icon: <PersonIcon />, to: "/profile" },
        { text: "Loja", icon: <DashboardIcon />, to: "/loja" },
      ];
    }

    if (userType === "partner") {
      return [
        { text: "Perfil", icon: <PersonIcon />, to: "/profile" },
        { text: "Dashboard", icon: <DashboardIcon />, to: "/dashboard" },
        { text: "Clientes", icon: <PeopleIcon />, to: "/dashboard/empresa/clientes" },
        { text: "Comissões", icon: <AttachMoneyIcon />, to: "/dashboard/empresa/comissoes" },
      ];
    }

    return [
      { text: "Perfil", icon: <PersonIcon />, to: "/profile" },
      { text: "Dashboard", icon: <DashboardIcon />, to: "/cliente" },
    ];
  }, [userType]);

  const isDesktopCollapsed = mdUp && collapsed;
  const drawerWidth = mdUp
    ? isDesktopCollapsed
      ? COLLAPSED_DRAWER_WIDTH
      : FULL_DRAWER_WIDTH
    : FULL_DRAWER_WIDTH;

  if (!currentUser) {
    return (
      <Box component="main" sx={{ p: 3 }}>
        {children}
      </Box>
    );
  }

  const drawerPaperStyles = {
    width: drawerWidth,
    boxSizing: "border-box",
    borderRight: `1px solid ${
      isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"
    }`,
    overflowX: "hidden",
    overflowY: "auto",
    background: isDark
      ? "linear-gradient(180deg,#020617 0%,#0f172a 58%,#111827 100%)"
      : "linear-gradient(180deg,#ffffff 0%,#f8fbff 58%,#edf3ff 100%)",
    transition: theme.transitions.create(["width"], {
      duration: theme.transitions.duration.shorter,
    }),
  };

  const UserAvatar = ({ size = 50, border = "3px solid #fff" }) => (
    <Avatar
      src={userType === "admin" ? brunoImg : undefined}
      sx={{
        width: size,
        height: size,
        border,
        boxShadow: "0 10px 22px rgba(15,23,42,0.12)",
        bgcolor: theme.palette.primary.main,
        color: "#fff",
        fontWeight: 900,
      }}
    >
      {userType !== "admin" ? avatarInitial : null}
    </Avatar>
  );

  const drawerContent = (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DrawerHeader>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            flex: 1,
            justifyContent: "flex-start",
          }}
        >
          <Box
            component="img"
            src={sintanetLogo}
            alt="Sintanet"
            sx={{
              width: 250,
              height: "auto",
              objectFit: "contain",
              display: "block",
              ml: 1,
              mt: -2,
            }}
          />
        </Box>
      </DrawerHeader>

      {!isDesktopCollapsed && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.6,
              borderRadius: "22px",
              bgcolor: isDark
                ? "rgba(255,255,255,0.045)"
                : "rgba(37,99,235,0.055)",
              border: `1px solid ${
                isDark
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(37,99,235,0.12)"
              }`,
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "17px",
                  bgcolor: alpha(theme.palette.success.main, 0.14),
                  color: theme.palette.success.main,
                }}
              >
                <ShieldRoundedIcon fontSize="small" />
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 950 }}>
                  Sistema online
                </Typography>

                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  Sincronizado em tempo real
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      )}

      <Box sx={{ px: 2, py: 1, flexGrow: 1, overflowX: "hidden" }}>
        <List sx={{ p: 0 }}>
          {navItems.map(({ text, icon, to, badge }) => {
            const selected = !!matchPath({ path: to, end: false }, pathname);

            return (
              <Tooltip
                key={text}
                title={isDesktopCollapsed ? text : ""}
                placement="right"
              >
                <ListItemButton
                  component={RouterLink}
                  to={to}
                  selected={selected}
                  onClick={!mdUp ? toggleMobile : undefined}
                  sx={{
                    minHeight: 64,
                    mb: 1.1,
                    px: isDesktopCollapsed ? 0 : 1.8,
                    justifyContent: isDesktopCollapsed ? "center" : "flex-start",
                    borderRadius: "20px",
                    position: "relative",
                    overflow: "hidden",
                    transition: "0.22s ease",

                    "&::before": selected
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 14,
                          bottom: 14,
                          width: 5,
                          borderRadius: 99,
                          bgcolor: theme.palette.primary.main,
                        }
                      : {},

                    "& .MuiListItemIcon-root": {
                      minWidth: 0,
                      mr: isDesktopCollapsed ? 0 : 1.65,
                      justifyContent: "center",
                      color: selected
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    },

                    "& .MuiSvgIcon-root": {
                      fontSize: 25,
                    },

                    "& .MuiListItemText-root": {
                      display: isDesktopCollapsed ? "none" : "block",
                    },

                    "& .MuiListItemText-primary": {
                      fontWeight: selected ? 950 : 800,
                      fontSize: 15,
                      color: selected
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                    },

                    "&.Mui-selected": {
                      bgcolor: alpha(
                        theme.palette.primary.main,
                        isDark ? 0.18 : 0.1
                      ),
                    },

                    "&.Mui-selected:hover": {
                      bgcolor: alpha(
                        theme.palette.primary.main,
                        isDark ? 0.22 : 0.14
                      ),
                    },

                    "&:hover": {
                      transform: "translateX(4px)",
                      bgcolor: alpha(
                        theme.palette.primary.main,
                        isDark ? 0.11 : 0.06
                      ),
                    },
                  }}
                >
                  <ListItemIcon>{icon}</ListItemIcon>

                  {!isDesktopCollapsed && (
                    <>
                      <ListItemText primary={text} />

                      {badge ? (
                        <Box
                          sx={{
                            minWidth: 26,
                            height: 26,
                            px: 0.7,
                            borderRadius: "999px",
                            bgcolor: theme.palette.primary.main,
                            color: "#fff",
                            display: "grid",
                            placeItems: "center",
                            fontSize: 13,
                            fontWeight: 950,
                          }}
                        >
                          {badge}
                        </Box>
                      ) : null}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Box sx={{ px: 2, pb: 2.2 }}>
        <Paper
          elevation={0}
          onClick={openMenu}
          sx={{
            p: 1.35,
            borderRadius: "22px",
            cursor: "pointer",
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: isDark ? "rgba(255,255,255,0.045)" : "#ffffff",
            transition: "0.22s ease",

            "&:hover": {
              transform: "translateY(-2px)",
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <UserAvatar size={50} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 15, fontWeight: 950 }}>
                {userName || "Utilizador"}
              </Typography>

              <Typography noWrap sx={{ fontSize: 12.5, color: "text.secondary" }}>
                {roleLabel}
              </Typography>
            </Box>

            <KeyboardArrowDownRoundedIcon sx={{ color: "text.secondary" }} />
          </Stack>
        </Paper>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: -1,
            minWidth: 240,
            borderRadius: "20px",
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: isDark
              ? "0 20px 50px rgba(0,0,0,0.45)"
              : "0 20px 50px rgba(15,23,42,0.12)",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.4 }}>
          <Typography sx={{ fontWeight: 950 }}>
            {userName || "Utilizador"}
          </Typography>

          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {currentUser?.email || roleLabel}
          </Typography>
        </Box>

        <Divider />

        <MenuItem component={RouterLink} to="/profile" onClick={closeMenu}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <PersonIcon fontSize="small" />
          </ListItemIcon>

          <ListItemText primary="Perfil" />
        </MenuItem>

        <MenuItem onClick={handleLogout}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>

          <ListItemText primary="Terminar sessão" />
        </MenuItem>
      </Menu>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: isDark ? "#020617" : "#f5f8ff",
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          height: APPBAR_HEIGHT,
          zIndex: (t) => t.zIndex.drawer + 1,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark
            ? "rgba(15,23,42,0.84)"
            : "rgba(255,255,255,0.88)",
          backdropFilter: "blur(20px)",

          ...(mdUp && {
            width: `calc(100% - ${drawerWidth}px)`,
            ml: `${drawerWidth}px`,
          }),
        }}
      >
        <Toolbar
          sx={{
            minHeight: `${APPBAR_HEIGHT}px !important`,
            px: { xs: 1.5, sm: 2, md: 3.5 },
            gap: { xs: 1, md: 2 },
          }}
        >
          <IconButton
            onClick={!mdUp ? toggleMobile : toggleCollapsed}
            sx={{
              width: { xs: 46, md: 52 },
              height: { xs: 46, md: 52 },
              borderRadius: { xs: "16px", md: "20px" },
              bgcolor: isDark ? "rgba(255,255,255,0.045)" : "#fff",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <MenuIcon />
          </IconButton>

          {!mdUp && (
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: 18,
                  fontWeight: 950,
                  lineHeight: 1.1,
                }}
              >
                Sintanet
              </Typography>

              <Typography
                noWrap
                sx={{
                  fontSize: 12,
                  color: "text.secondary",
                  fontWeight: 700,
                }}
              >
                {roleLabel}
              </Typography>
            </Box>
          )}

          {mdUp && <Box sx={{ flexGrow: 1 }} />}

          <Stack
            direction="row"
            spacing={{ xs: 0.8, md: 1.4 }}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            {mdUp && (
              <>
                <Chip
                  icon={<CalendarMonthRoundedIcon />}
                  label={new Date().toLocaleDateString("pt-PT", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                  sx={{
                    height: 48,
                    px: 1.2,
                    borderRadius: "16px",
                    bgcolor: isDark ? "rgba(255,255,255,0.045)" : "#ffffff",
                    border: `1px solid ${theme.palette.divider}`,
                    fontWeight: 850,
                  }}
                />

                <Chip
                  label="Online"
                  icon={
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: theme.palette.success.main,
                      }}
                    />
                  }
                  sx={{
                    height: 48,
                    px: 1.4,
                    borderRadius: "16px",
                    bgcolor: isDark ? "rgba(255,255,255,0.045)" : "#ffffff",
                    border: `1px solid ${theme.palette.divider}`,
                    fontWeight: 900,

                    "& .MuiChip-icon": {
                      ml: 1,
                    },
                  }}
                />
              </>
            )}

            {userType === "admin" && (
              <Tooltip title="Notificações">
                <IconButton
                  component={RouterLink}
                  to="/admin/notificacoes"
                  sx={{
                    width: { xs: 46, md: 54 },
                    height: { xs: 46, md: 54 },
                    borderRadius: "50%",
                    bgcolor: isDark ? "rgba(255,255,255,0.045)" : "#ffffff",
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Badge badgeContent={3} color="primary">
                    <NotificationsActiveIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Conta">
              <IconButton onClick={openMenu} sx={{ p: 0.4 }}>
                <UserAvatar
                  size={mdUp ? 58 : 46}
                  border={mdUp ? "4px solid #fff" : "3px solid #fff"}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      {mdUp ? (
        <Box sx={{ width: drawerWidth, flexShrink: 0 }}>
          <Drawer
            variant="permanent"
            open
            sx={{
              width: drawerWidth,
              flexShrink: 0,

              "& .MuiDrawer-paper": {
                ...drawerPaperStyles,
                width: drawerWidth,
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </Box>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={toggleMobile}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              ...drawerPaperStyles,
              width: FULL_DRAWER_WIDTH,
              maxWidth: "88vw",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: {
            xs: "100%",
            md: `calc(100% - ${drawerWidth}px)`,
          },
          p: contentPaper ? { xs: 1.25, sm: 2, md: 3 } : 0,
          position: "relative",
          overflowX: "hidden",
        }}
      >
        <Toolbar sx={{ minHeight: `${APPBAR_HEIGHT}px !important` }} />

        {contentPaper ? (
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              borderRadius: { xs: "20px", md: "28px" },
              border: `1px solid ${theme.palette.divider}`,
              p: { xs: 1.5, sm: 2, md: 3 },
              minHeight: `calc(100vh - ${APPBAR_HEIGHT + 32}px)`,
              overflow: "hidden",
              bgcolor: isDark
                ? "rgba(15,23,42,0.90)"
                : "rgba(255,255,255,0.78)",
              backdropFilter: "blur(18px)",
            }}
          >
            {children}
          </Paper>
        ) : (
          <Box sx={{ width: "100%" }}>{children}</Box>
        )}
      </Box>
    </Box>
  );
}